
import { Check, ShoppingBag } from '@phosphor-icons/react';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { useState } from 'react';

import { useCart } from '@/components/cart-provider';
import { Button, ButtonLink, Callout } from '@/components/ui';
import { cn } from '@/lib/cn';

export function BuyPanel({
  publishedId,
  sizes,
  inStock,
}: {
  publishedId: string;
  sizes: string[];
  inStock: boolean;
}) {
  const navigate = useNavigate();
  const router = useRouter();
  const { add } = useCart();
  const [size, setSize] = useState<string | null>(sizes.length === 1 ? sizes[0]! : null);
  const [qty, setQty] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState(false);

  function addToCart() {
    if (!size) {
      setError('Choose a size first.');
      return;
    }
    setError(null);
    add({ published_id: publishedId, size, qty });
    setAdded(true);
    router.invalidate();
    window.setTimeout(() => setAdded(false), 2400);
  }

  return (
    <div className="space-y-6">
      <fieldset>
        <legend className="text-sm font-semibold">
          Size
          {size && <span className="ml-1.5 font-normal text-muted">{size}</span>}
        </legend>
        {/* Radio semantics so the choice is announced and keyboard-navigable,
            styled as the prototype's size chips. */}
        <div className="mt-2.5 flex flex-wrap gap-2" role="radiogroup" aria-label="Size">
          {sizes.map((s) => (
            <button
              key={s}
              type="button"
              role="radio"
              aria-checked={size === s}
              onClick={() => {
                setSize(s);
                setError(null);
              }}
              className={cn(
                'h-12 min-w-[52px] rounded-[10px] border px-3.5 text-[15px] font-semibold transition-colors',
                size === s
                  ? 'border-brand bg-brand-wash text-brand-ink'
                  : 'border-line-strong bg-panel hover:border-ink/40',
              )}
            >
              {s}
            </button>
          ))}
        </div>
      </fieldset>

      <div>
        <span id="qty-label" className="text-sm font-semibold">
          Quantity
        </span>
        {/* Stepper, not a dropdown: one press per unit at this scale. */}
        <div className="mt-2.5 inline-flex items-center rounded-[10px] border border-line-strong bg-panel">
          <button
            type="button"
            onClick={() => setQty((q) => Math.max(1, q - 1))}
            className="grid size-12 place-items-center text-xl font-bold hover:bg-raised disabled:opacity-40"
            aria-label="Decrease quantity"
            disabled={qty <= 1}
          >
            &minus;
          </button>
          <output
            aria-labelledby="qty-label"
            className="w-12 text-center font-mono text-base font-bold"
          >
            {qty}
          </output>
          <button
            type="button"
            onClick={() => setQty((q) => Math.min(99, q + 1))}
            className="grid size-12 place-items-center text-xl font-bold hover:bg-raised"
            aria-label="Increase quantity"
          >
            +
          </button>
        </div>
      </div>

      {error && <Callout tone="danger" title={error} />}

      {inStock ? (
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={addToCart} size="lg">
            {added ? (
              <>
                <Check size={18} weight="bold" aria-hidden />
                Added to cart
              </>
            ) : (
              <>
                <ShoppingBag size={18} weight="bold" aria-hidden />
                Add to cart
              </>
            )}
          </Button>
          <ButtonLink href="/cart" weight="secondary" size="lg">
            View cart
          </ButtonLink>
        </div>
      ) : (
        <Callout tone="warn" title="Out of stock">
          This one is held in stock and we have sold out. Start a custom order and we can
          print the same garment to order.
        </Callout>
      )}

      <p aria-live="polite" className="sr-only">
        {added ? 'Added to cart' : ''}
      </p>
    </div>
  );
}
